import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Grid, Header, Icon, Label, Menu, Message, Segment } from 'semantic-ui-react';
import S from './core.js';
import { ACCOUNT_MANAGERS, UNASSIGNED, managerOf } from './constants.js';
import { detectMode, getReport, keyOfSummary, listReports, resetDemo, toModel } from './api.js';
import { fileBase, makeExcel, makePdf, makeSummaryExcel, makeZip, saveFile } from './exportFiles.js';
import { addApproval, statusOf } from './approvals.js';
import { defaultSelection, hasFigures, periodOf, viewLayout } from './periods.js';
import { loadDb, resetDb, saveDb, storageLabel } from './store/dbStore.js';
import AccountManagerList from './components/AccountManagerList.jsx';
import ManagerReports from './components/ManagerReports.jsx';
import UploadForm from './components/UploadForm.jsx';
import Preview from './components/Preview.jsx';
import PeriodPicker from './components/PeriodPicker.jsx';
import ApprovalPanel from './components/ApprovalPanel.jsx';
import ApprovalHistory from './components/ApprovalHistory.jsx';
import SummaryTab from './components/SummaryTab.jsx';
import { useAuth } from './auth/AuthContext.js';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EMPTY_DB = { app_user: [], report_approval: [] };
const slug = (t) => t.replace(/[^A-Za-z0-9]+/g, '_');
const keyOfReport = (r) => `${r.settlement_report_id}/${r.report_year}/${r.as_at_date}`;
const USER_KEY = 'srs_demo_user';
const savedUser = () => { try { return window.localStorage.getItem(USER_KEY); } catch { return null; } };

export default function App() {
  const [reports, setReports] = useState([]);
  const [model, setModel] = useState(null);          // the report on screen (read-only): { report, figures, ... }
  const [sel, setSel] = useState(null);              // the period being viewed: { mode, year, quarter, month }
  const [view, setView] = useState('reports');       // 'reports' | 'summary'
  const [status, setStatus] = useState(null);        // {text, kind}
  const [busy, setBusy] = useState(null);            // 'load' | 'finance' | 'account_manager' | 'xlsx' | 'pdf' | 'bulk' | 'summary'
  const [manager, setManager] = useState(null);       // account manager whose Reports screen is showing
  const [mode, setMode] = useState(null);            // null while detecting, then 'live' or 'demo' (no backend)
  const [db, setDb] = useState(EMPTY_DB);             // JSON "database": app_user + report_approval tables
  const [demoUserId, setDemoUserId] = useState(savedUser);
  const demo = mode === 'demo';
  const auth = useAuth();                              // the signed-in Microsoft user (null when sign-in is switched off)
  const timer = useRef(null);

  const say = useCallback((text, kind = 'info') => {
    setStatus({ text, kind });
    clearTimeout(timer.current);
    if (kind !== 'err') timer.current = setTimeout(() => setStatus(null), 5000);
  }, []);

  const refreshList = useCallback(async () => {
    try { setReports(await listReports()); } catch (e) { say(`Could not load the report list: ${e.message}`, 'err'); }
  }, [say]);
  useEffect(() => {                                    // use the backend if it answers, otherwise the built-in demo data
    let active = true;
    (async () => {
      const m = await detectMode();
      if (!active) return;
      setMode(m);
      refreshList();
      try { const d = await loadDb(); if (active) setDb(d); } catch (e) { say(`Could not load approvals: ${e.message}`, 'err'); }
    })();
    return () => { active = false; };
  }, [refreshList, say]);

  /* ----------------------------------------------------------------- who is acting */
  const user = useMemo(() => {
    if (auth) {                                        // real sign-in: match the Microsoft account to app_user by e-mail
      const who = String(auth.username || '').toLowerCase();
      return db.app_user.find((u) => u.email.toLowerCase() === who) || null;
    }
    return db.app_user.find((u) => u.user_id === demoUserId) || db.app_user[0] || null;
  }, [auth, db.app_user, demoUserId]);
  const pickUser = (id) => { setDemoUserId(id); try { window.localStorage.setItem(USER_KEY, id); } catch { /* storage blocked */ } };

  /* ---------------------------------------------------------------- derived */
  const groups = useMemo(() => {                       // the left-hand menu: each account manager with their reports
    const by = new Map(ACCOUNT_MANAGERS.map((n) => [n, []]));
    reports.forEach((r) => { const n = managerOf(r); if (!by.has(n)) by.set(n, []); by.get(n).push(r); });
    return [...by].filter(([n, list]) => n !== UNASSIGNED || list.length).map(([name, list]) => ({ name, reports: list }));
  }, [reports]);

  const report = model?.report;
  const base = useMemo(() => (model ? S.buildLayout(model.report, model.figures) : null), [model]);
  const period = useMemo(() => (sel ? periodOf(sel) : null), [sel]);
  const approvalStatus = useMemo(() => (report && period ? statusOf(db.report_approval, report, period) : null), [db.report_approval, report, period]);
  const layout = useMemo(() => (base && period ? viewLayout(base, period, approvalStatus) : null), [base, period, approvalStatus]);
  const figuresHere = useMemo(() => (base && period ? hasFigures(base, period) : false), [base, period]);
  const totalsCheck = useMemo(() => (model ? S.checkTotals(model.figures, model.report) : []), [model]);
  const years = useMemo(() => (report ? [...new Set(reports.filter((r) => r.settlement_report_id === report.settlement_report_id).map((r) => r.report_year))].sort((a, b) => b - a) : []), [reports, report]);
  const history = useMemo(() => (report ? db.report_approval.filter((a) => a.settlement_report_id === report.settlement_report_id && a.report_year === report.report_year && a.as_at_date === report.as_at_date) : []), [db.report_approval, report]);

  /* ---------------------------------------------------------------- loading */
  const loadFull = useCallback(async (summary) => toModel(await getReport(keyOfSummary(summary))), []);
  async function open(summary, nextSel) {
    setBusy('load');
    try {
      const m = { ...(await loadFull(summary)), key: keyOfSummary(summary) };
      setModel(m); setSel(nextSel || defaultSelection(m.report)); setManager(null); setView('reports'); setStatus(null);
    } catch (e) { say(`Could not open the report: ${e.message}`, 'err'); } finally { setBusy(null); }
  }
  function changePeriod(next) {
    if (next.year === report.report_year) { setSel(next); return; }
    const sibling = reports.filter((r) => r.settlement_report_id === report.settlement_report_id && r.report_year === next.year)
      .sort((a, b) => b.as_at_date.localeCompare(a.as_at_date))[0];
    if (sibling) open(sibling, next);
  }
  const selectManager = (name) => { setManager(name); setView('reports'); setStatus(null); };

  /* ---------------------------------------------------------------- approvals */
  async function approve(level) {
    const label = level === 'finance' ? 'Finance' : 'Account Manager';
    if (!window.confirm(`Approve ${report.settlement_report_id} – ${period.title} as ${label}, ${user.display_name}?\nThe approval is recorded with your name and the date and time, and cannot be undone here.`)) return;
    setBusy(level);
    try {
      const fresh = await loadDb();                    // re-read first, so an approval made elsewhere in the meantime is respected
      addApproval(fresh, { level, report, period, user });
      await saveDb(fresh);
      setDb(fresh);
      say(`${label} approval recorded for ${period.title}.`, 'ok');
    } catch (e) { say(`Could not record the approval: ${e.message}`, 'err'); } finally { setBusy(null); }
  }

  /* ------------------------------------------------------------ export */
  async function download(kind) {
    if (approvalStatus.stage !== 'complete') return;   // belt and braces: the buttons are not shown before this
    setBusy(kind);
    try {
      const name = `${fileBase(report)}_${slug(period.title)}`;
      if (kind === 'xlsx') saveFile(`${name}.xlsx`, await makeExcel(layout), XLSX);
      else saveFile(`${name}.pdf`, await makePdf(layout), 'application/pdf');
      say(`Built ${name}.${kind} for ${period.title}.`, 'ok');
    } catch (e) { say(`Could not build the file: ${e.message}`, 'err'); } finally { setBusy(null); }
  }
  async function generateAll(list) {                   // one Annual PDF per ticked (fully approved) report; several are bundled in a .zip
    setBusy('bulk');
    try {
      const files = [];
      const rows = (await loadDb()).report_approval;
      for (const summary of list) {
        const m = toModel(await getReport(keyOfSummary(summary)));
        const p = periodOf({ mode: 'annual', year: m.report.report_year });
        const st = statusOf(rows, m.report, p);
        if (st.stage !== 'complete') continue;
        files.push({ name: `${fileBase(m.report)}_${slug(p.title)}.pdf`, data: await makePdf(viewLayout(S.buildLayout(m.report, m.figures), p, st)) });
      }
      if (!files.length) throw new Error('None of the selected reports is fully approved.');
      if (files.length === 1) saveFile(files[0].name, files[0].data, 'application/pdf');
      else saveFile(`${manager.replace(/[^A-Za-z0-9_.-]+/g, '_')}_settlement_reports.zip`, await makeZip(files), 'application/zip');
      say(`Generated ${files.length} PDF${files.length === 1 ? '' : 's'}${files.length > 1 ? ' (in one .zip)' : ''}.`, 'ok');
    } catch (e) { say(`Could not generate the PDFs: ${e.message}`, 'err'); } finally { setBusy(null); }
  }
  async function downloadSummary(summary) {
    setBusy('summary');
    try {
      const name = `Summary_${summary.year}-${String(summary.monthIdx + 1).padStart(2, '0')}.xlsx`;
      saveFile(name, await makeSummaryExcel(summary), XLSX);
      say(`Built ${name}.`, 'ok');
    } catch (e) { say(`Could not build the file: ${e.message}`, 'err'); } finally { setBusy(null); }
  }

  async function resetDemoData() {
    if (!window.confirm('Reset the demo reports and all recorded approvals to the original sample?')) return;
    resetDemo(); setModel(null); setSel(null); setStatus(null);
    try { await resetDb(); setDb(await loadDb()); } catch (e) { say(`Could not reset the approvals: ${e.message}`, 'err'); return; }
    refreshList();
    say('Demo data and approvals reset to the original sample.', 'ok');
  }

  const showingReport = view === 'reports' && !manager && model;
  return (
    <>
      <a className="skip" href="#main">Skip to the report</a>
      <Menu as="header" inverted borderless className="topbar">
        <Menu.Item as="h1" header><Icon name="chart bar outline" />Settlement Report Studio{demo && <Label color="orange" size="tiny" className="demolabel">DEMO</Label>}</Menu.Item>
        <Menu.Menu role="group" aria-label="Sections">
          <Menu.Item as="button" type="button" className="navbtn" active={view === 'reports'} aria-current={view === 'reports' ? 'page' : undefined} onClick={() => setView('reports')}>Reports</Menu.Item>
          <Menu.Item as="button" type="button" className="navbtn" active={view === 'summary'} aria-current={view === 'summary' ? 'page' : undefined} onClick={() => setView('summary')}>Summary</Menu.Item>
        </Menu.Menu>
        <Menu.Menu position="right" role="group" aria-label="Account">
          {auth ? (
            <>
              <Menu.Item className="user"><Icon name="user circle" />{auth.name}{user ? ` (${user.role === 'finance' ? 'Finance' : 'Account Manager'})` : ''}</Menu.Item>
              <Menu.Item><Button type="button" basic inverted compact size="small" content="Sign out" onClick={auth.signOut} /></Menu.Item>
            </>
          ) : (
            <Menu.Item className="user">
              <Icon name="user circle" />
              <label htmlFor="acting-as" className="actinglabel">Acting as</label>
              <select id="acting-as" className="ui dropdown" value={user?.user_id || ''} onChange={(e) => pickUser(e.target.value)}>
                {db.app_user.map((u) => <option key={u.user_id} value={u.user_id}>{u.display_name} – {u.role === 'finance' ? 'Finance' : 'Account Manager'}</option>)}
              </select>
            </Menu.Item>
          )}
        </Menu.Menu>
      </Menu>

      {demo && (
        <Message info className="demobar" role="note">
          <strong>Demo mode</strong> – no backend is connected. These are dummy reports modelled on the Excel template; they are read-only here.
          Approvals are saved in {storageLabel()}. Use “Acting as” (top right) to approve as Finance or as an Account Manager.{' '}
          <Button type="button" basic compact size="mini" onClick={resetDemoData}>Reset demo data</Button>
        </Message>
      )}

      <Grid stackable padded>
        <Grid.Column computer={4} tablet={5} mobile={16}>
          <aside aria-label="Account managers and uploads">
            <Segment><UploadForm demo={demo} onDone={(out) => { refreshList(); say(`${out.results.filter((r) => r.status === 'loaded').length} report(s) loaded.`, 'ok'); }} onError={(m) => say(m, 'err')} /></Segment>
            <AccountManagerList groups={groups} activeManager={view === 'reports' ? manager : null} onSelectManager={selectManager} onSelectReport={(r) => open(r)}
              currentKey={showingReport ? keyOfReport(report) : null} />
          </aside>
        </Grid.Column>

        <Grid.Column computer={12} tablet={11} mobile={16}>
          <main id="main" aria-busy={busy === 'load'}>
            {view === 'summary' ? (
              <SummaryTab reports={reports} approvals={db.report_approval} loadFull={loadFull} onExcel={downloadSummary} busy={busy} />
            ) : manager ? (
              <ManagerReports key={manager} manager={manager} reports={groups.find((g) => g.name === manager)?.reports || []}
                approvals={db.report_approval} busy={busy} onOpen={(r) => open(r)} onGenerate={generateAll} />
            ) : !model ? (
              <Segment placeholder>
                <Header icon><Icon name="user circle outline" />Choose an account manager on the left to see their reports, or open the Summary.</Header>
              </Segment>
            ) : (
              <>
                <Header as="h2" className="titlebar">
                  {report.settlement_report_id}
                  <Header.Subheader>
                    {report.client_name} · {report.report_year} · as at <time dateTime={report.as_at_date}>{S.dmy(report.as_at_date)}</time> · {managerOf(report)}
                  </Header.Subheader>
                </Header>
                <PeriodPicker sel={sel} years={years} onChange={changePeriod} disabled={!!busy} />
                {totalsCheck.length > 0 && sel.mode !== 'monthly' && (
                  <Message warning role="note">
                    <strong>{totalsCheck.length} stored totals don’t match the months.</strong> In the sample this is the template’s Q4 formula (it adds Aug–Oct instead of Oct–Dec).
                  </Message>
                )}
                <ApprovalPanel period={period} report={report} status={approvalStatus} user={user} busy={busy} hasFigures={figuresHere}
                  onApprove={approve} onDownload={download} storage={storageLabel()} />
                {!figuresHere && <Message info role="status">This report has no figures for {period.title} (it runs to {S.dmy(report.as_at_date)}).</Message>}
                <Preview layout={layout} />
                <ApprovalHistory rows={history} />
              </>
            )}
          </main>
        </Grid.Column>
      </Grid>

      {status && (status.kind === 'err'
        ? (
          <Message negative role="alert" className="toast">
            {status.text} <Button type="button" basic compact size="mini" onClick={() => setStatus(null)}>Dismiss</Button>
          </Message>
        ) : <Message as="output" aria-live="polite" success={status.kind === 'ok'} info={status.kind === 'info'} className="toast">{status.text}</Message>)}
    </>
  );
}
